import {
  Badge,
  Button,
  Flex,
  IconButton,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from "@chakra-ui/react";
import {
  List,
  ListFiltersContextProvider,
  useListFilters,
  useUserSettingsWithFallback,
} from "@questy/aura-cl";
import { CredentialContextProvider, useCredentials } from "@questy/lib";
import { filter } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { FaTrash } from "react-icons/fa";
import { CustomViewOfferPriceListAddNew } from "./addNew";
import { customOfferPriceEditor, deleteCustomOfferPrice } from "./api";
import { useViewOfferPriceListContext } from "./context";

export const CustomViewOfferPriceListList = () => {
  const { data, meta, additionalData, refreshData } =
    useViewOfferPriceListContext();
  const listFilters = useListFilters();
  const [filtersData, setFiltersData] = useState<string[]>([]);
  const [sortData, setSortData] = useState<string>("order[id]=desc");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);

  // filtry
  useEffect(() => {
    let filtersTmp: string[] = [];
    const filters = listFilters?.filters;
    // id
    if (filters?.id?.value?.from)
      filtersTmp.push(`filter[id][gte]=${filters.id.value.from}`);
    if (filters?.id?.value?.to)
      filtersTmp.push(`filter[id][lte]=${filters.id.value.to}`);

    // nazwa klienta
    if (filters?.company_name?.value)
      filtersTmp.push(
        `filter[accountCompany.name][hasText]=${filters.company_name.value}`
      );

    // nazwa projektu
    if (filters?.project_name?.value)
      filtersTmp.push(
        `filter[projectName][hasText]=${filters.project_name.value}`
      );

    //   usługa
    if (filters?.service_name?.value)
      filtersTmp.push(
        `filter[service.name][hasText]=${filters.service_name.value}`
      );

    // pozycja
    if (filters?.product_name?.value)
      filtersTmp.push(
        `filter[product.name][hasText]=${filters.product_name.value}`
      );

    // opis
    if (filters?.description?.value)
      filtersTmp.push(
        `filter[description][hasText]=${filters.description.value}`
      );

    // cena
    if (filters?.price?.value?.from)
      filtersTmp.push(`filter[price][gte]=${filters.price.value.from}`);
    if (filters?.price?.value?.to)
      filtersTmp.push(`filter[price][lte]=${filters.price.value.to}`);

    // data
    if (filters?.date?.value?.from)
      filtersTmp.push(
        `filter[startDate][eq]=${moment(filters.date.value.from).format(
          "YYYY-MM-DD"
        )}`
      );
    if (filters?.date?.value?.to)
      filtersTmp.push(
        `filter[endDate][eq]=${moment(filters.date.value.to).format(
          "YYYY-MM-DD"
        )}`
      );

    // status
    if (filters?.status?.value) {
      const value = filters.status.value;
      if (!(value?.length > 1)) {
        filtersTmp.push(`filter[is_active][eq]=${value}`);
      }
    }

    // sprawdzanie czy filtersData jest różne od filtersTmp
    if (filtersData.join("") !== filtersTmp.join("")) {
      setFiltersData(filtersTmp);
    }
  }, [listFilters.filters]);

  // zastosowanie dodatkowych danych
  useEffect(() => {
    let temp = [...filtersData, sortData];

    if (page != 1) {
      temp.push(`page[number]=${page}`);
    }
    if (pageSize != 20) {
      temp.push(`page[limit]=${pageSize}`);
    }
    additionalData.set(temp);
  }, [filtersData, sortData, page, pageSize]);

  return (
    <CredentialContextProvider module="commissions">
      <ListFiltersContextProvider value={listFilters}>
        <List
          data={data ?? []}
          columns={[
            {
              id: "id",
              Header: <>Identyfikator</>,
              accessor: (e) => <Link>{e?.id}</Link>,
            },
            {
              id: "accountCompany.name",
              Header: <>Nazwa klienta</>,
              accessor: (e) => (
                <Link
                  title={e?.accountCompany?.name}
                  href={`/accountCompany/show/company_id/${e?.accountCompany?.id}`}
                >
                  {e?.accountCompany?.name}
                </Link>
              ),
            },
            {
              id: "projectName",
              Header: <>Nazwa projektu</>,
              accessor: "projectName",
            },
            {
              id: "realizationNumber",
              Header: <>Numer realizacyjny</>,
              accessor: "realizationNumber",
            },
            {
              id: "service.name",
              Header: <>Usługa</>,
              accessor: "service.name",
            },
            {
              id: "product.name",
              Header: <>Pozycja</>,
              accessor: "product.name",
            },
            {
              id: "description",
              Header: <>Opis</>,
              accessor: "description",
            },
            {
              id: "price",
              Header: <>Cena</>,
              accessor: "price",
            },
            {
              id: "startDate",
              Header: <>Data rozpoczęcia</>,
              accessor: (e) =>
                moment(e?.startDate).isValid()
                  ? moment(e.startDate).format("YYYY-MM-DD")
                  : "",
            },
            {
              id: "endDate",
              Header: <>Data zakończenia</>,
              accessor: (e) =>
                moment(e?.endDate).isValid()
                  ? moment(e.endDate).format("YYYY-MM-DD")
                  : "",
            },
            {
              id: "is_active",
              Header: <>Status</>,
              accessor: (e) =>
                e?.is_active ? (
                  <Badge colorScheme="green">Aktywny</Badge>
                ) : (
                  <Badge colorScheme="red">Nieaktywny</Badge>
                ),
            },
            {
              id: "action",
              Header: <>Akcje</>,
              accessor: (e) => (
                <Popover>
                  <PopoverTrigger>
                    <IconButton
                      colorScheme="red"
                      aria-label="Delete"
                      size="sm"
                      onClick={() => {}}
                      icon={<FaTrash />}
                    />
                  </PopoverTrigger>
                  <PopoverContent>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverHeader>
                      Napewno chcesz usunąć cennik ?
                    </PopoverHeader>
                    <PopoverBody>
                      <Button
                        onClick={() => {
                          customOfferPriceEditor().then(() => {
                            deleteCustomOfferPrice(e.id).then(() => {
                              refreshData();
                            });
                          });
                        }}
                        colorScheme={"red"}
                      >
                        Usuń
                      </Button>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              ),
            },
            {
              id: "commission.name",
              width: 200,
              Header: <>Proces</>,
              accessor: (e) => (
                <Link
                  title={e?.commission?.name}
                  href={`/commission/show/commission_id/${e?.commission?.id}`}
                >
                  {e?.commission?.commissionNumber} [{e?.commission?.name}]
                </Link>
              ),
            },
          ]}
          handleGoToNextPage={() => {
            // tylko w przypadku gdy nie jesteśmy na ostatniej stronie
            if (page < meta?.page?.total_number) setPage(page + 1);
          }}
          handleGoToPrevPage={() => {
            // tylko w przypadku gdy nie jesteśmy na pierwszej stronie
            if (page > 1) setPage(page - 1);
          }}
          handleSetPage={(e) => {
            // tylko gdy mieści się w zakresie
            if (e >= 1 && e <= meta?.page?.total_number) setPage(e);
          }}
          handleSetPageSize={(e) => {
            setPageSize(e);
          }}
          pageSize={pageSize}
          isLoading={data == null}
          listSorting={[]}
          module="custom_offer_price_lists"
          moduleName="Cenniki ofert"
          pageCount={meta?.page?.total_number ?? 1}
          setProfileSetting={async (e) => {}}
          totalResults={meta?.page?.total_count ?? 0}
          setSorting={(e) => {
            const sort = e?.[0];
            const tmp = `order[${sort?.id}]=${sort.desc ? "desc" : "asc"}`;
            if (tmp != sortData) setSortData(tmp);
          }}
          settings={{}}
          setUserSetting={async (e) => {}}
          listFiltersConfig={[
            { id: "id", name: "Identyfikator", type: "integer" },
            {
              id: "company_name",
              name: "Nazwa klienta",
              type: "string",
            },
            {
              id: "project_name",
              name: "Nazwa projektu",
              type: "string",
            },
            { id: "service_name", name: "Usługa", type: "string" },
            { id: "product_name", name: "Pozycja", type: "string" },
            { id: "description", name: "Opis", type: "string" },
            { id: "price", name: "Cena", type: "integer" },
            { id: "date", name: "Data obowiązywania", type: "date" },
            {
              id: "status",
              name: "Status",
              type: "list",
              additionalData: {
                positions: [
                  {
                    id: "true",
                    value: "Aktywny",
                    ordering: 0,
                  },
                  {
                    id: "false",
                    value: "Nieaktywny",
                    ordering: 1,
                  },
                ],
              },
            },
          ]}
          mainAction={
            <Flex gap={"10px"}>
              <CustomViewOfferPriceListAddNew />
              <Button
                colorScheme={"teal"}
                onClick={() => {
                  customOfferPriceEditor().then(() => {
                    window.location.href =
                      "/xlsxImport/load/from_shortcut/true/xlsx_import_template_id/29";
                  });
                }}
              >
                Importuj cennik
              </Button>
              <Button
                onClick={() => {
                  window.location.href =
                    "/historia%20zmian%20cenników%20ofert.html";
                }}
              >
                Historia cenników
              </Button>
            </Flex>
          }
          additionalActions={[]}
        />
      </ListFiltersContextProvider>
    </CredentialContextProvider>
  );
};
